// Copyright: IQGeo Limited 2010-2019
import _ from 'underscore';
import Promise from 'bluebird';
import myw from 'myWorld/base/core';
import config from 'myWorld/base/config';
import './datasource';
import 'myWorld/features/myWorldFeature';
import 'myWorld/features/traceResult';
import 'myWorld/layers/styleManager';
import 'myWorld/layers/tileLayer';
import 'myWorld/layers/directoryTileLayer';
import 'myWorld/layers/mywVectorLayer';
import 'myWorld/layers/hybridLayer';
import 'myWorld/layers/mywVectorLayerLabeled';
import 'myWorld/layers/geoserverLayer';

myw.MyWorldDatasource = myw.Datasource.extend('MyWorldDatasource', {
    defaultFeatureModel: myw.MyWorldFeature,

    statics: {
        layerDefFields: [
            {
                name: 'rendering',
                type: 'string',
                enumerator: {
                    vector: { group: 'datasourceConfigOptions', key: 'vector' },
                    tilestore: { group: 'datasourceConfigOptions', key: 'tilestore' },
                    tile_directory: { group: 'datasourceConfigOptions', key: 'tile_directory' },
                    geoserver: { group: 'datasourceConfigOptions', key: 'geoserver' }
                },
                default: 'vector',
                viewClass: 'RenderingEditor',
                nativeAppRenderingFields: [
                    {
                        name: 'jsClass',
                        type: 'string'
                    },
                    {
                        name: 'extraOptions',
                        type: 'json',
                        viewClass: 'KeyValueView',
                        args: { keyTitle: 'name', valueTitle: 'value', valType: 'json' }
                    }
                ],
                onChange: 'rebuildForm'
            },
            {
                name: 'layer',
                type: 'string',
                condition(def) {
                    return (
                        _.contains(['overlay', 'basemap'], def.category) &&
                        def.spec &&
                        def.spec.rendering == 'tilestore'
                    );
                }
            },
            {
                name: 'jsClass',
                type: 'string'
            },
            {
                name: 'extraOptions',
                type: 'json',
                viewClass: 'KeyValueView',
                args: { keyTitle: 'name', valueTitle: 'value', valType: 'json' }
            },
            {
                name: 'isStatic',
                type: 'boolean',
                default: false,
                condition(def) {
                    return def.spec && def.spec.rendering == 'vector';
                }
            },
            {
                name: 'nativeAppMode',
                type: 'string',
                default: 'switchable',
                enumerator: {
                    switchable: { group: 'datasourceConfigOptions', key: 'switchable' },
                    master: { group: 'datasourceConfigOptions', key: 'master' },
                    local: { group: 'datasourceConfigOptions', key: 'local' }
                },
                condition(def) {
                    if (def.spec) return _.includes(['vector', 'tilestore'], def.spec.rendering);
                }
            },
            {
                name: 'tileDirUrl',
                type: 'string',
                size: 'long',
                condition(def) {
                    if (def.spec) return def.spec.rendering == 'tile_directory';
                }
            },
            {
                name: 'geoserverName',
                type: 'string',
                enumerator: 'datasource.geoserverNames',
                condition(def) {
                    return def.spec && def.spec.rendering == 'geoserver';
                }
            },
            {
                name: 'geoserverWorkspace',
                type: 'string',
                condition(def) {
                    return def.spec && def.spec.rendering == 'geoserver';
                }
            },
            {
                name: 'geoserverLayer',
                type: 'string',
                condition(def) {
                    return def.spec && def.spec.rendering == 'geoserver';
                }
            }
        ],
        specFields: [
            {
                name: 'tilestore',
                type: 'json',
                viewClass: 'KeyValueView',
                args: {
                    isArray: true,
                    keyProp: 'layers',
                    valueProp: 'file',
                    keyTitle: 'layers',
                    valueTitle: 'file'
                }
            },
            {
                name: 'geoserverUrls',
                type: 'json',
                viewClass: 'GeoserverURLTable',
                args: { keyTitle: 'name', valueTitle: 'url' }
            }
        ]
    },

    options: {
        maxSuggestionsPerType: 10,
        geoserverUrls: {}
    },

    /**
     * @class Datasource to provide visualisation, selection and search on a remote myWorld server </br>
     * @param  {myWorldDatasourceOptions}    options
     * @constructs
     * @augments IDatasource
     * @augments Datasource
     */
    initialize(database, options) {
        myw.Datasource.prototype.initialize.call(this, database, options);

        this.layerCodes = [];
        this.layerNames = [];
        this.featureTypes = [];

        this.styleManager = new myw.StyleManager();

        this.geoserverNames = _.keys(this.options.geoserverUrls);

        //bind methods so we don't need to do it later
        _.bindAll(this, 'asFeatures', 'asTraceResult');

        this.initialized = Promise.resolve(this._getServer())
            .then(database.system.getStartupInfo.bind(database.system, database.applicationName))
            .then(startupInfo => {
                this.startupInfo = startupInfo;
                return this;
            });
    },

    /**
     * Obtains the JavaScript class to instantiate a feature of the given type <br/>
     * _ensureDDInfoFor() should be called beforehand
     *  Overridden to create an 'unahtorized' dd for feature types that we couldn't obtain a dd
     * @param  {string}     [featureType]     Type of feature
     * @return {Class}
     * @protected
     */
    _getFeatureClassFor(featureType) {
        const featureDD = this.featuresDD[featureType];
        if (!featureDD) {
            this.featuresDD[featureType] = {
                unauthorized: true
            };
        }

        return myw.Datasource.prototype._getFeatureClassFor.call(this, featureType);
    },

    /* ****************************** Interface methods **************************** */

    /**
     * Returns the credentials to use for the login request
     * Returns true if it needs credentials by user
     * Returns false if login is not necessary
     * @return {Promise<boolean>}  Keyed on field name
     */
    getLoginCredentials() {
        return this.server.isLoggedIn().then(
            (
                isLoggedIn //if not logged in we need credentials from user
            ) =>
                //if already logged in, no credentials are necessary
                !isLoggedIn
        );
    },

    /**
     * Obtain details about fields necessary to perform a login request
     * @return {promise}
     */
    getAuthOptions() {
        return this.server.getAuthOptions();
    },

    /**
     * Logs in to the datasource
     * @param  {object} credentials
     * @return {promise}
     */
    login(credentials) {
        return this.server.login(credentials);
    },

    /**
     * Instantiates a leaflet layer from a layer definition
     * @param  {layerDefinition} layerDef
     * @return {TileLayer|MywVectorLayer}  The instantiated layer
     * @private
     */
    createLayer(layerDef) {
        let layer;
        const layerDefFixedOptions = myw.Util.processOptionsFromJson(layerDef.extraOptions);
        _.extend(layerDef.options, layerDefFixedOptions); //ENH: move to myw.Layer.initialize

        if (myw.isNativeApp && layerDef.mode === 'local' && layerDef.nativeAppVector) {
            layer = this._createNativeAppLayer(layerDef);
        } else if (layerDef.rendering == 'tilestore') {
            //tiles myWorld tile datastore
            layer = this._createTileLayer(layerDef);
        } else if (layerDef.rendering == 'tile_directory') {
            //Tiles served directly from directory structure with tiles
            const Layer = this._getLayerClassFor(layerDef, myw.DirectoryTileLayer);
            layer = new Layer(layerDef.tileDirUrl, layerDef.options);
        } else if (layerDef.rendering == 'geoserver') {
            //tiles server via geoserver
            layer = this._createGeoserverLayer(layerDef);
        } else {
            //vector
            layer = this._createVectorLayer(layerDef);
        }

        this._registerLayer(layerDef);

        return layer;
    },

    /**
     * Whether this datasource allows editing of features
     * @return {Boolean}
     */
    isEditable() {
        return this.server.isMasterDatabase() || this.server.isReplicaDatabase();
    },

    /**
     * Obtains a feature
     * @param  {string}     featureType
     * @param  {string|integer}    featureId    Key that identifies feature in table
     * @param  {boolean}    [includeLobs=true]  Whether large object fields should be included or not
     * @param  {string}     [delta]             Id of delta to obtain the feature from (overrides database's delta)
     * @return {promise<MyWorldFeature>}
     */
    getFeature(featureType, featureId, includeLobs, delta) {
        includeLobs = includeLobs !== false; //default is true

        return this.server
            .getFeature(this.getName(), featureType, featureId, includeLobs, delta)
            .then(data => this.asFeatures(data, true, includeLobs))
            .then(features => {
                if (features.length > 0) return features[0];
            });
    },

    /**
     * Get features of a given table optionally constrained by bounding box
     * @param  {string}             featureType
     * @param  {queryParameters}    [options]       Filters to apply.
     * @return {Promise<Array<Feature>>}    Promise to resolve with a list of the matched features
     */
    getFeatures(featureType, options) {
        options = _.extend({ dsName: this.getName() }, options);

        this._processGetFeaturesDefaultOptions(options);
        if (options.limit === null) delete options.limit;
        //null means not limited
        else options.limit = options.limit || config['core.queryResultLimit']; //ENH: receive in this.options instead of accesing config

        //ENH: convert clauses into filter so that server doesn't need to handle clauses

        return this.server.getFeatures(featureType, options).then(this.asFeatures);
    },

    /**
     * Get features given a list or urns
     * @param {string[]} urns
     * @param {queryParameters} options Only options controlling aspects to obtained are used
     */
    async getFeaturesByUrn(urns, options) {
        options = options || {};
        this._processGetFeaturesDefaultOptions(options);
        const urnsByType = _.groupBy(urns, urn => urn.split('/')[0]);
        const types = Object.keys(urnsByType);
        const results = await Promise.map(types, type => {
            const urns = urnsByType[type];
            const ids = urns.map(urn => urn.split('/')[1]);
            return this.server.getFeaturesByUrn(type, ids, options);
        });
        const features = _.flatten(_.pluck(results, 'features'));
        return this.asFeatures({ features });
    },

    //sets defaults for getFeatures requests in a given 'options' object
    _processGetFeaturesDefaultOptions(options) {
        if (typeof options.displayValues == 'undefined')
            //unspecified, default to if there is any registered interest
            options.displayValues = this.database.existsInterestIn('display_values');
        else options.displayValues = !!options.displayValues; //ensure boolean
        options.includeLobs = !!options.includeLobs; //default false
        options.includeGeoGeometry = !!options.includeGeoGeometry; //default false
    },

    /**
     * Count features of a given table optionally constrained by bounding box
     * @param  {string}             featureType
     * @param  {queryParameters}    [options]       Filters to apply.
     * @return {Promise<integer>}    Promise to resolve with a number of the matched features
     */
    countFeatures(featureType, options) {
        options = _.extend({ dsName: this.getName() }, options);

        return this.server.countFeatures(featureType, options);
    },

    /**
     * Finds the features selectable by a user map click
     * @param  {L.LatLng}   selectionPoint      Point the user clicked/selected
     * @param  {integer}    zoomLevel           Zoom level at time of selection
     * @param  {integer}    pixelTolerance  Number of pixels to use as tolerance for the selection
     * @param  {array<Layer|string>}   layers      Layers relevant for selection (active and visible)
     * @param  {string}     [worldId]           Defaults to geographical world
     * @return {Promise<Array<Feature>>}  Promise for the features
     */
    async select(selectionPoint, zoomLevel, pixelTolerance, layers, worldId) {
        const layerIds = this._getLayerIds(layers);
        const args = [worldId, selectionPoint, zoomLevel];
        let features = [];
        if (layerIds.length) {
            const res = await this.server.selection(...args, layerIds, pixelTolerance);
            if (res && res.features) features = features.concat(res.features);
        }

        //handle selection in layers configured for delta schema
        const deltaLayerIds = this._getLayerIds(layers, 'delta');
        if (deltaLayerIds.length) {
            const deltaArgs = [...args, deltaLayerIds, pixelTolerance, 'delta'];
            const res = await this.server.selection(...deltaArgs);

            if (res && res.features) features = features.concat(res.features);
        }

        return this.asFeatures({ features }, false);
    },

    _getLayerIds(layers, schema = 'data') {
        return _.compact(
            layers.map(layer => {
                if (typeof layer == 'string') return layer;
                const layerSchema =
                    (layer.layerDef.extraOptions && layer.layerDef.extraOptions.schema) || 'data';
                if (layerSchema == schema) return layer.getCode();
            })
        );
    },

    /**
     * Finds the features selectable by box select
     * @param  {L.LatLngBounds} bounds  Bounds to select inside of
     * @param  {integer}    zoomLevel           Zoom level at time of selection
     * @param  {array<Layer|string>}   layers      Layers relevant for selection (active and visible)
     * @param  {string}     [worldId]           Defaults to geographical world
     * @return {Promise<Array<Feature>>}  Promise for the features
     */
    async selectBox(bounds, zoomLevel, layers, worldId) {
        const layerIds = this._getLayerIds(layers);

        const limit = config['core.queryResultLimit']; //max number of features selectable
        let features = [];

        if (layerIds.length) {
            const res = await this.server.selectBox(bounds, zoomLevel, layerIds, limit, worldId);
            if (res && res.features) features = features.concat(res.features);
        }

        //handle selection in layers configured for delta schema
        const deltaLayerIds = this._getLayerIds(layers, 'delta');
        if (deltaLayerIds.length) {
            const args = [bounds, zoomLevel, deltaLayerIds, limit, worldId, 'delta'];
            const res = await this.server.selectBox(...args);
            if (res && res.features) features = features.concat(res.features);
        }

        return this.asFeatures({ features }, false);
    },

    /**
     * Sends a search request
     * @param  {string}         searchTerm      Text to search for
     * @param  {searchOptions}  [options]       Options to influence the search
     * @return {Promise<Array<autoCompleteResult>>}  Promise for autocomplete suggestions to present the user
     */
    runSearch(searchTerm, options) {
        options = _.extend(
            {
                limit: this.options.maxSuggestionsPerType
            },
            options
        );

        return this.server
            .runSearch(this.getName(), searchTerm.toLowerCase(), options)
            .then(this._addFeatureSearchSuggestion.bind(this, searchTerm, options.limit))
            .then(this._parseSuggestions.bind(this));
    },

    /**
     * Sends a query request on a given feature type
     * @param  {queryDefinition}  queryDef   As generated by results of runSearch()
     * @param  {queryOptions}     [options]
     * @return {Promise<array<MyWorldFeature>>} Promise for a list of features
     */
    runQuery(queryDef, options) {
        const params = {
            filter: queryDef.filter,
            includeGeoGeometry: true
        };

        if (options.bounds) params.bounds = options.bounds;
        if (options.polygon) params.geom = options.polygon;
        if (options.displayValues) params.displayValues = options.displayValues;

        return this.getFeatures(queryDef.feature_type, params);
    },

    /**
     * Sends a feature search request that covers multiple feature types
     * @param  {string}         text    Text to search for
     * @param  {searchOptions}  [options]  Note: bounds options is not used (won't bias results)
     * @return {Promise<array<MyWorldFeature>>} Promise for a list of features
     */
    getFeaturesMatching(text, options) {
        options = _.extend({}, options);

        return this.server
            .getFeaturesMatching(this.getName(), text.toLowerCase(), options)
            .then(this.asFeatures);
    },

    /**
     * Obtains features of a given type that are close to a point and within a tolerance
     * @param  {string[]}   featureTypes     Types of features to obtain
     * @param  {L.LatLng}   position
     * @param  {Integer}    tolerance       Tolerance in meters
     * @return {Promise<MyWorldFeature[]>}    Promise for a list with the features which are close to the given point within the specified tolerance
     */
    getFeaturesAround(featureTypes, position, tolerance) {
        //ENH: make this just one call
        const requests = featureTypes.map(featureType => {
            return this.server
                .getFeaturesAround(featureType, position, tolerance)
                .then(data => data.features);
        });

        return Promise.all(requests)
            .then(results => ({
                features: _.flatten(results)
            }))
            .then(this.asFeatures);
    },

    /**
     * Delete a feature by it's id
     * @param  {string}   featureType
     * @param  {string}   featureId
     * @return {Promise}    Promise which will resolve when the operation has completed
     */
    _deleteFeature(featureType, featureId) {
        return this.server.deleteFeature(featureType, featureId);
    },

    /**
     * Insert a feature into a table
     * @param  {string}   featureType
     * @param  {featureData}   insertData
     * @param  {boolean}   [update=false] If true, an id is provided and feature already exits, update it
     * @return {Promise<integer>}    Promise for the id of the inserted feature
     */
    _insertFeature(featureType, insertData, update = false) {
        return this._prepareValues(featureType, true, insertData).then(convertedData =>
            this.server.insertFeature(featureType, convertedData, update)
        );
    },

    /**
     * Update a feature in a table
     * @param  {string}   featureType
     * @param  {string}   featureId
     * @param  {featureData}   updateData
     * @return {Promise<boolean>}    Promise for the success of the operation
     */
    _updateFeature(featureType, featureId, updateData) {
        return this._prepareValues(featureType, false, updateData).then(convertedData =>
            this.server.updateFeature(featureType, featureId, updateData)
        );
    },

    /**
     * Returns the features in a relationship with a given feature
     * @param  {Feature}    feature             Feature for which we want the related records
     * @param  {string}         relationshipName    Name of relationship (field)
     * @return {Promise<Feature[]>}    Promise for a list with the features in the relationship
     */
    getRelationship(feature, relationshipName) {
        const fieldDD = feature.featureDD.fields[relationshipName];

        if (!fieldDD) {
            throw new Error(
                `No relationship '${relationshipName}' for feature type: ${feature.getType()}`
            );
        } else if (fieldDD.value) {
            //calculated field
            return this._getCalculatedRelationship(feature, fieldDD);
        } else if (fieldDD.type.indexOf('foreign_key(') === 0) {
            //stored foreign_key to single (myWorld) feature
            return this.server.getRelationship(feature, relationshipName).then(this.asFeatures);
        } else if (fieldDD.type == 'reference') {
            //stored reference to single feature
            const urn = feature.properties[relationshipName];
            if (!urn) return Promise.resolve([]);
            return this.database.getFeatureByUrn(urn).then(feature => (feature ? [feature] : []));
        } else {
            //stored reference to multiple features
            return this._getReferenceSetRelationship(feature, relationshipName);
        }
    },

    /**
     * Get features of a given table for vector rendering
     * @param  {renderParams}    params         Render parameters
     * @return {Promise<Array<Feature>>}    Promise to resolve with a list of the matched features
     */
    getLayerFeatures(params) {
        params.limit = params.limit || config['core.queryResultLimit'];

        return this.server
            .getLayerFeatures(params)
            .then(data =>
                this.asFeatures(data.featureCollection, true).then(features =>
                    Promise.resolve({ features: features, offset: data.offset })
                )
            );
    },

    /**
     * Returns a new transaction object
     * @return {Transaction}
     */
    transaction() {
        return new myw.Transaction(this.database);
    },

    /**
     * Run (insert, delete, update) operations on multiple features within one transaction in the database
     * @param {Transaction} transaction     Operations to be executed
     * @return {Promise<Integer[]>}  Promise which resolves with ids
     */
    runTransaction(transaction) {
        return transaction
            .getOperations()
            .map(operation => {
                //adjust operation, converting values as necessary
                let toGenerate = false;
                const op = operation[0];
                const featureType = operation[1];
                const feature = operation[2];
                if (op == 'insert') {
                    toGenerate = true;
                }
                if (op == 'delete' || op == 'deleteIfExists') {
                    return operation;
                }

                return this._prepareValues(featureType, toGenerate, feature).then(convertedData => {
                    convertedData.type = 'Feature';
                    return [op, featureType, convertedData];
                });
            })
            .then(operations => this.server.runTransaction(operations));
    },

    /**
     * Returns the networks a given feature can be part of
     * @param  {MyWorldFeature} feature
     * @return {promise}         Network properties keyed on network name
     */
    getNetworksFor(feature) {
        return this.server.getNetworksFor(feature);
    },

    /**
     * Find connected network objects
     * @param {string}    network  Name of network to trace through
     * @param {Feature|string}    feature  Start feature
     * @param {boolean}  [options.direction='downstream'] Direction to trace in (upstream|downstream|both)
     * @param {number}   [options.maxDist]  Max distance to trace to, in meters
     * @param {string}   [options.resultType='features']  Structure of results: 'features' or 'tree'
     * @param {string[]} [options.resultFeatureTypes]  Feature types to include in result
     * @param {object<object>} [options.filters]  Filters keyed on feature type
     * @return {Promise<Array<Feature>>}  Connected features
     */
    traceOut(network, feature, options) {
        // direction, maxDist, resultType, filters, returnTypes

        this.system.consumeLicence(this.database.applicationName, 'core.network_trace');

        let featureUrn = feature;
        if (typeof feature.getUrn === 'function') {
            featureUrn = feature.getUrn();
        }

        options = _.defaults(options || {}, {
            direction: 'downstream',
            resultType: 'features',
            maxNodes: config['core.plugin.trace.limit']
        });

        const handleResult =
            options.resultType == 'tree' ? this.asTraceResult : result => this.asFeatures(result);

        return this.server.traceOut(network, featureUrn, options).then(handleResult);
    },

    /**
     * Find shortest path through a network
     * @param {string}    network  Name of network to trace through
     * @param {Feature}    feature  Start feature
     * @param {string}    toUrn  URN of destination feature
     * @param {number}   [options.maxDist]  Max distance to trace to, in meters
     * @param {string}   [options.resultType='features']  Structure of results: 'features' or 'tree'
     * @return {Promise<Array<Feature>>}  Path to destination feature (empty if not reachable)
     */
    shortestPath(network, feature, toUrn, options) {
        this.system.consumeLicence(this.database.applicationName, 'core.network_trace');

        options = _.defaults(options || {}, {
            resultType: 'features',
            maxNodes: config['core.plugin.trace.limit']
        });

        const handleResult =
            options.resultType == 'tree' ? this.asTraceResult : result => this.asFeatures(result);

        return this.server.shortestPath(network, feature, toUrn, options).then(handleResult);
    },

    /**
     * Invokes a custom module controller via a GET request
     * @param {string} url
     * @param {object} params
     */
    moduleGet(url, params) {
        return this.server.moduleGet(url, params);
    },

    /**
     * Invokes a custom module controller via a PUT request
     * @param {string} url
     * @param {object} params
     */
    modulePut(url, data) {
        return this.server.modulePut(url, data);
    },

    /**
     * Invokes a custom module controller via a POST request
     * @param {string} url
     * @param {object} params
     */
    modulePost(url, data) {
        return this.server.modulePost(url, data);
    },

    /* ******************************** Auxiliary methods ****************************** 
    /**
     * Sets 'this.server' to be a database instance for the url specified in options. <br/>
     * Performs a login request with the parameters specified in options
     * @return {Promise}  Promise fulfilled when the database is instantiated and initialized
     * @protected
     */
    _getServer() {
        if (this.options.server) {
            //use existing server (tests)
            this.server = this.options.server;
        } else {
            //no server given, use same server as system (application page)
            this.server = this.system.server;
        }

        return this.server.initialized;
    },

    /**
     * Instantiates a layer for native app - local mode to display features rendered as vector, from a myWorld server
     * @param  {layerDefinition} layerDef
     * @return {MywVectorLayer} The instantiated layer
     * @private
     */
    _createNativeAppLayer(layerDef) {
        layerDef.jsClass = layerDef.nativeAppVector.jsClass;
        layerDef.extraOptions = layerDef.nativeAppVector.extraOptions;
        layerDef.isStatic = true; //Alternate/hybrid vector layers are always static

        if (layerDef.nativeAppVector.fromScale > layerDef.min_scale) {
            const Layer = this._getLayerClassFor(layerDef, myw.HybridLayer);
            return new Layer(this, layerDef);
        } else {
            return this._createVectorLayer(layerDef);
        }
    },

    /**
     * Instantiates a layer to display tiles served by myWorld
     * @param  {layerDefinition} layerDef
     * @return {TileLayer}  The instantiated layer
     * @private
     */
    _createTileLayer(layerDef) {
        const layerOptions = this.server.getTileLayerOptions(layerDef);
        const Layer = this._getLayerClassFor(layerDef, layerOptions.classPrototype);
        return new Layer(layerOptions.url, layerOptions);
    },

    /**
     * Instantiates a layer to display features, rendered as vector, from a myWorld server
     * @param  {layerDefinition} layerDef
     * @return {MywVectorLayer}  The instantiated layer
     * @private
     */
    _createVectorLayer(layerDef) {
        const options = _.extend(
            {
                name: layerDef.name, //used for debugging
                isStatic: !!layerDef.isStatic,
                featureTypes: layerDef.feature_types
            },
            layerDef.options
        );
        const Layer = this._getLayerClassFor(layerDef, myw.MywVectorLayer);

        return new Layer(this, this.styleManager, options);
    },

    /**
     * Instantiates a layer to display features, rendered via a geoserver server
     * @param  {layerDefinition} layerDef
     * @return {promise<L.TileLayer.WMS>}  The instantiated layer
     * @private
     */

    _createGeoserverLayer(layerDef) {
        return this._getFilters(layerDef.feature_types, layerDef.options.schema).then(filters => {
            const prefix = layerDef.geoserverWorkspace
                ? `${layerDef.geoserverWorkspace}:`
                : layerDef.options.schema == 'delta'
                ? 'myworld_delta:'
                : '';
            const layerOptions = this.options.geoserverUrls[layerDef.geoserverName];
            //  Prior to 5.2, this was a string and not an object
            let url, auth;
            if (typeof layerOptions === 'string') {
                url = layerOptions;
                auth = { type: 'none' };
            } else if (typeof layerOptions === 'object') {
                url = layerOptions.url;
                auth = layerOptions.auth;
            }
            if (!url) throw new myw.URLNotDefinedError();

            const geoserverLayer = layerDef.geoserverLayer || '';
            const overriddenFeatureItems = geoserverLayer
                .split(',')
                .filter(String)
                .map(name => ({
                    name
                }));

            if (layerDef.geoserverLayer) filters = undefined;
            const options = _.extend(
                {
                    filters: filters,
                    featureItems: overriddenFeatureItems.length
                        ? overriddenFeatureItems
                        : layerDef.feature_types,
                    prefix,
                    transparent: layerDef.options.zIndex ? layerDef.options.zIndex >= 0 : true,
                    format: 'image/png',
                    startupInfo: this.startupInfo,
                    applicationName: this.database.applicationName,
                    ds: this,
                    auth
                },
                layerDef.options
            );
            const Layer = this._getLayerClassFor(layerDef, myw.GeoserverLayer);

            return new Layer(url, options);
        });
    },

    /**
     * @param  {featureTypeDef[]} featureTypes Feature types information
     * @return {promise<filterDef[]>}
     * @private
     */
    _getFilters(featureTypes, schema) {
        const filterArray = _.filter(featureTypes, featureType => featureType.filter);
        if (filterArray.length !== 0 || schema == 'delta') {
            return this.getDDInfoFor(_.pluck(filterArray, 'name')).then(featureDDs => {
                const filters = _.map(featureTypes, featureType => {
                    const filterName = featureType.filter;
                    if (filterName || schema == 'delta') {
                        //Compile filters and add filtering of current delta if schema is delta
                        const featureDD = featureDDs[featureType.name];
                        let filter;
                        if (featureDD && featureDD.filters) filter = featureDD.filters[filterName];
                        if (schema == 'delta' && !filter) {
                            filter = '[myw_delta] <> {activeDelta}';
                        } else if (schema == 'delta') {
                            //Add filter to current filters
                            filter = '(' + filter + ')'; //Need to do in two steps otherwise Javascript looses the first bracket (annoyingly)
                            filter += '& [myw_delta] <> {activeDelta}';
                        }
                        return {
                            layerName: featureType.name,
                            value: filter //use composed filter
                        };
                    }
                });
                return filters;
            });
        } else {
            return Promise.resolve([]).bind(this);
        }
    },

    /**
     * Register that a layer is using this datasource
     * @param  {layerDefinition} layerDef
     * @private
     */
    _registerLayer(layerDef) {
        //ENH: check if layerCodes is still used
        if (!_.contains(this.layerCodes, layerDef.code)) {
            this.layerCodes.push(layerDef.code);
            this.layerNames.push(layerDef.name);
        }

        //store the definition in case it's a dynamically added layer (non stored in configuration)
        this.layerDefs[layerDef.name] = layerDef;

        const layerFeatureTypes = _.pluck(layerDef.feature_types, 'name');
        this.featureTypes = _.uniq(this.featureTypes.concat(layerFeatureTypes));
    },

    /**
     * add "features matching" suggestion if/where appropriate
     * @private
     */
    _addFeatureSearchSuggestion(searchText, limit, results) {
        //finding first feature suggestion
        const featureIndex = _.findIndex(results, sugg => sugg.data.type == 'feature');
        if (featureIndex >= 0) {
            let featureResults = results.slice(featureIndex);

            const //"features matching" suggestion to be added just before the first feature suggestion
                featureSearchSuggestion = {
                    label: searchText,
                    value: searchText,
                    type: 'feature_search'
                };

            if (featureResults.length === limit) {
                //Replace the last suggestion with a '...' suggestion
                featureResults = _.without(featureResults, featureResults.pop());
                const moreResultsSuggestion = {
                    label: '',
                    value: searchText,
                    type: 'feature_search'
                };
                results = results
                    .slice(0, featureIndex)
                    .concat(featureSearchSuggestion, featureResults, moreResultsSuggestion);
            } else if (featureResults.length > 1) {
                // just add the "features matching" suggestion
                results = results
                    .slice(0, featureIndex)
                    .concat(featureSearchSuggestion, featureResults);
            }
        }
        return results;
    },

    /**
     * Converts results from server into
     * @return {array<autoCompleteResult>}
     * @private
     */
    async _parseSuggestions(results) {
        const to_return = [];
        for (let result of results) {
            const data = result.data,
                type = result.type || data.type;
            //results from server have 'type' inside 'data'. convert into a proper {autoCompleteResult}
            if (data && data.type) delete data.type;
            result.type = type;
            //Set geometry_type on result
            let featureData;
            if (data && data.feature_type) {
                featureData = await this.getDDInfoFor([data && data.feature_type]);
                result.data.has_geometry = !!featureData[data.feature_type].geometry_type;
            }

            to_return.push(result);
        }
        return to_return;
    },

    /**
     * Obtains the features in a stored feature set field
     * @param  {Feature} feature
     * @param  {string} relationshipName Field name
     * @return {Promise<Array<Feature>>}
     */
    _getReferenceSetRelationship(feature, relationshipName) {
        const urns = feature.properties[relationshipName] || [];

        // map each urn to the feature:
        // - external features must be requested one by one
        // - all the myWorld features can be requested in a single request and then
        //   features looked up by urn in the result. The request to myWorld is only
        //   if there is a myWorld urn in the list

        let myWorldRequest;
        const featuresPromise = Promise.all(
            urns.map(urn => {
                const dsName = this.database.getDatasourceNameForUrn(urn);
                if (this.getName() == dsName) {
                    if (!myWorldRequest) {
                        //execute myworld server relationship request, storing results in a hash by urn
                        myWorldRequest = this.server
                            .getRelationship(feature, relationshipName)
                            .then(this.asFeatures)
                            .then(features => _.indexBy(features, feature => feature.getUrn()));
                    }
                    return myWorldRequest.then(features => features[urn.split('?')[0]]);
                } else {
                    return this.database.getFeatureByUrn(urn);
                }
            })
        );
        return featuresPromise.then(_).call('compact'); //filter out missing features
    },

    /**
     * Obtains the features in a calculated relationship
     * @param  {feature} feature
     * @param  {fieldDD} fieldDD
     * @return {Promise<Array<Feature>>}
     * @private
     */
    _getCalculatedRelationship(feature, fieldDD) {
        if (fieldDD.value.indexOf('select(') === 0) {
            //query reference_set
            const relationshipName = fieldDD.internal_name;
            return this.server.getRelationship(feature, relationshipName).then(this.asFeatures);
        } else if (fieldDD.value.indexOf('method(') === 0) {
            //custom reference_set
            const methodName = fieldDD.value.slice(7).split(')')[0];

            if (typeof feature[methodName] !== 'function') {
                return Promise.reject(
                    new Error(
                        `Expected feature '${feature.getUrn()}'' to have method named '${methodName}'`
                    )
                );
            } else {
                return feature[methodName]().then(result => {
                    if (result instanceof myw.Feature) return [result];
                    else return result;
                });
            }
        } else {
            return Promise.reject(
                new Error(`Unexpected value '${fieldDD.value}' for field: ${fieldDD.internal_name}`)
            );
        }
    },

    /**
     * Returns a list of myWorldFeatures from the provided list of json features
     * @param  {Object}     jsonResults         Objects obtained from parsing json results
     * @param  {boolean}    [complete=true]     Whether the data includes all 'simple' properties or not
     * @param  {boolean}    [lobs=false]        Whether the data includes all 'large object' properties or not
     * @return {Promise<Array<MyWorldFeature>>}
     * @private
     */
    asFeatures(jsonResults, complete, lobs) {
        const featuresData = jsonResults.features || [jsonResults]; // We may get a group of features back or we might get just one feature.
        const options = {
            complete: complete !== false, //default is true
            lobs: !!lobs //default is false
        };

        return this._asFeatures(featuresData, options).then(features => {
            features.offset = jsonResults.offset + 1; // server is 0 based
            features.totalCount = jsonResults.unlimited_count || Infinity;
            return features;
        });
    },

    /*
     * @param  {Object}     featuresData        Objects obtained from parsing json results
     * @param  {Object}     options             Options to be passed to feature model constructor
     * @return {Promise<Array<MyWorldFeature>>}
     * @private
     */
    async _asFeatures(featuresData, options) {
        //get list of different feature types
        let types = featuresData.map(featureData => featureData.myw.feature_type);
        types = _.uniq(types);

        await this._ensureDDInfoFor(types);

        return _.map(featuresData, featureData => {
            const type = featureData.myw.feature_type;
            delete featureData.myw.feature_type;
            return this._asFeature(featureData, type, options);
        });
    },

    /**
     * @param  {json} jsonResults Result of trace service request
     * @return {promise<TraceResult>}
     */
    async asTraceResult(jsonResults) {
        const jsonFeatures = _.values(jsonResults.features);
        await myw.geometry.init(); // TraceResult may need to perform geometry operations
        const features = await this._asFeatures(jsonFeatures, true, false);
        return new myw.TraceResult(jsonResults.nodes, features, jsonResults.metadata);
    },

    /**
     * Converts properties for an insert/update into a standard format
     * In particular it converts date fields to a standardized string format
     * @param  {string}     featureType
     * @param  {boolean}    generators  Whether to populate values for fields with (client-side) generators
     * @param  {Object}     data
     * @return {Object}           [description]
     * @private
     */
    async _prepareValues(featureType, generators, data) {
        const featuresDD = await this.getDDInfoFor([featureType]);
        const featureDD = featuresDD[featureType];

        this._convertValues(featureDD, data);

        if (featureDD.fields.myw_geometry_world_name && data.geometry) {
            data.properties.myw_geometry_world_name = data.geometry.world_name || 'geo';
        }

        if (generators) this._populateGeneratorFields(featureDD, data.properties);

        return data;
    },

    /**
     * Converts properties for an insert/update into a standard format
     * In particular it converts date fields to a standardized string format
     * @param  {featureDD} featureDD
     * @param  {Object} data
     * @return {Object} The modified 'data' object
     * @private
     */
    _convertValues(featureDD, data) {
        for (const key in data.properties) {
            const value = data.properties[key];
            const fieldDD = featureDD.fields[key];
            const fieldType = fieldDD && fieldDD.type;

            if (fieldDD && fieldDD.value) {
                //exclude calculated fields from data to send
                delete data.properties[key];
            } else if (fieldType == 'date' && value instanceof Date) {
                data.properties[key] = value.toISOString().slice(0, 10);
            } else if (fieldType == 'timestamp' && value instanceof Date) {
                data.properties[key] = value.toISOString().slice(0, -1); //removes 'Z' from the end
            }
        }

        return data;
    },

    /**
     * Converts properties for an insert/update into a standard format
     * In particular it converts date fields to a standardized string format
     * @param  {featureDD}  featureDD
     * @param  {Object}     properties
     * @return {Object}     The modified 'properties' object
     * @private
     */
    _populateGeneratorFields(featureDD, properties) {
        for (const key in featureDD.fields) {
            const fieldDD = featureDD.fields[key];
            if (fieldDD.generator == 'application') {
                properties[key] = myw.app && myw.app.name;
            } else if (fieldDD.generator == 'user') {
                properties[key] = myw.currentUser.username;
            }
        }
        return properties;
    }
});

//define getters and setters
Object.defineProperty(myw.MyWorldDatasource.prototype, 'delta', {
    // database version for all operations.
    get: function() {
        return this.server.delta;
    },

    // set database version for all operations. delta is a string, '' means master
    set: function(value) {
        if (value) this.system.consumeLicence(this.database.applicationName, 'core.versioning');
        this.server.delta = value;
    }
});

myw.datasourceTypes['myworld'] = myw.MyWorldDatasource;

/**
 * Options for {@link MyWorldDatasource}
 * @typedef myWorldDatasourceOptions
 * @property {integer}   maxSuggestionsPerType              Max number of results per suggestion type. May still be truncated by the search UI configuration
 * @property {object<string, string>}   geoserverUrls
 * @property {string}    [username]                         If provided, basic authentication credentials will be sent with requests. When using IE browsers, basic authentication requires jsonp to be disabled
 * @property {string}    [password]                         Required if username is provided
 */

export default myw.MyWorldDatasource;
